'use client';

import Link from 'next/link';
import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion, useScroll, useSpring } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';
import { SophiaLogo } from './SophiaLogo';

export function Nav() {
  const { locale, setLocale } = useLocale();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { damping: 30, stiffness: 180, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggle = () => setLocale(locale === 'en' ? 'ar' : 'en');
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const navLinks = [
    { href: '#features', label: dict.nav.features },
    { href: '#how', label: dict.nav.howItWorks },
    { href: '#retailers', label: dict.nav.retailers },
    { href: '#faq', label: dict.nav.faq },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[60] transition-all duration-500 ease-luxe ${
        scrolled || menuOpen
          ? 'bg-obsidian-900/90 backdrop-blur-md border-b border-white/10 shadow-[0_1px_12px_rgba(0,0,0,0.4)]'
          // At-top state: subtle-but-present backdrop so nav content never
          // bleeds into the hero eyebrow / headline visually.
          : 'bg-obsidian-900/55 backdrop-blur-sm border-b border-white/5'
      }`}
    >
      {/* Scroll progress — thin gold line across the top */}
      <motion.div
        aria-hidden
        style={{ scaleX: progress, transformOrigin: 'left' }}
        className="absolute top-0 inset-x-0 h-[1.5px] bg-gold-gradient"
      />
      <nav className="mx-auto max-w-7xl px-5 sm:px-6 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 group">
          <SophiaLogo size={32} className="shadow-glow rounded-luxe" />

          <span className="font-serif text-xl tracking-tight text-fog-100">
            Sophia
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8 text-sm text-fog-300">
          {navLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className="hover:text-fog-100 transition-colors duration-150 ease-out"
            >
              {t(label, locale)}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="press px-3 py-1.5 text-xs tracking-wide uppercase text-fog-300 hover:text-gold-soft hover:border-gold/30 transition-[color,border-color,transform] duration-150 ease-out rounded-full border border-white/10"
            aria-label="Toggle language"
          >
            {locale === 'en' ? 'عربي' : 'EN'}
          </button>
          <a
            href="https://chrome.google.com/webstore/detail/bcemapjfjdkcfffmmimnaoknicppgapg"
            target="_blank"
            rel="noopener noreferrer"
            className="press-lift hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-pill bg-cta-gradient text-white text-sm font-semibold hover:bg-cta-gradient-hover transition-[background,transform,box-shadow] duration-200 ease-out shadow-glow"
          >
            {t(dict.nav.install, locale)}
          </a>
          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="press md:hidden w-9 h-9 rounded-lg border border-white/10 grid place-items-center text-fog-300 hover:text-fog-100 hover:border-white/20 transition-[color,border-color,transform] duration-150 ease-out"
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              {menuOpen ? (
                <>
                  <line x1="4" y1="4" x2="14" y2="14" />
                  <line x1="14" y1="4" x2="4" y2="14" />
                </>
              ) : (
                <>
                  <line x1="3" y1="5" x2="15" y2="5" />
                  <line x1="3" y1="9" x2="15" y2="9" />
                  <line x1="3" y1="13" x2="15" y2="13" />
                </>
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-white/5 bg-ink-900/95 backdrop-blur-md"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map(({ href, label }) => (
                <a
                  key={href}
                  href={href}
                  onClick={closeMenu}
                  className="text-fog-300 hover:text-fog-100 transition-colors duration-150 ease-out py-2 text-sm"
                >
                  {t(label, locale)}
                </a>
              ))}
              <a
                href="https://chrome.google.com/webstore/detail/bcemapjfjdkcfffmmimnaoknicppgapg"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="press-lift inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-pill bg-cta-gradient text-white text-sm font-semibold shadow-glow mt-1 transition-transform duration-200 ease-out"
              >
                {t(dict.nav.install, locale)}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
